/**
 * offlineCache — read-through cache so the app keeps working without a
 * connection (App Review 4.2: native apps must not be dead without
 * internet). Whitelisted GET responses are persisted to localStorage;
 * when a request fails with a network error, the cached copy is served
 * and the response is tagged `fromOfflineCache` so UIs can badge it.
 */
const PREFIX = "akr_cache:";
const MAX_ENTRY_BYTES = 250_000; // skip giant payloads
const MAX_ENTRIES = 120;

// GET url paths (relative to /api) worth caching for offline reading.
const WHITELIST = [
  /^\/dogs(\/|\?|$)/,
  /^\/litters(\/|\?|$)/,
  /^\/care\//,
  /^\/calendar\/events/,
  /^\/me\/nav-context/,
  /^\/auth\/me/,
  /^\/pedigree/,
  /^\/billing\/my-subscription/,
];

const keyFor = (config) => {
  const url = config.url || "";
  const params = config.params ? JSON.stringify(config.params) : "";
  const uid = (() => {
    try { return localStorage.getItem("token")?.slice(-12) || "anon"; } catch { return "anon"; }
  })();
  return `${PREFIX}${uid}:${url}${params}`;
};

export const isCacheable = (config) => {
  if ((config?.method || "get").toLowerCase() !== "get") return false;
  const url = config?.url || "";
  return WHITELIST.some((re) => re.test(url));
};

export const saveToCache = (config, data) => {
  try {
    const body = JSON.stringify({ t: Date.now(), data });
    if (body.length > MAX_ENTRY_BYTES) return;
    localStorage.setItem(keyFor(config), body);
  } catch {
    // Quota exceeded — evict our oldest entries and give up quietly.
    try { pruneCache(true); } catch { /* private mode */ }
  }
};

export const readFromCache = (config) => {
  try {
    const raw = localStorage.getItem(keyFor(config));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

export function pruneCache(aggressive = false) {
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(PREFIX)) {
      let t = 0;
      try { t = JSON.parse(localStorage.getItem(k))?.t || 0; } catch { /* corrupt */ }
      entries.push({ k, t });
    }
  }
  const keep = aggressive ? Math.floor(MAX_ENTRIES / 2) : MAX_ENTRIES;
  if (entries.length <= keep) return;
  entries.sort((a, b) => a.t - b.t);
  entries.slice(0, entries.length - keep).forEach(({ k }) => localStorage.removeItem(k));
}
